<script lang="ts">
  /**
   * An accessory on an avatar (amendment 23): on the head, over the eyes, at an angle. Placed in
   * per cent of the avatar's `--size`, so it sits the same on a chat line and in the shop.
   *
   * Where an emoji is the thing — a crown, a top hat — it is the emoji. The rest is drawn: there
   * is no emoji for a party hat, a halo or devil's horns, and none for what a glider pilot
   * wears, the leather flying helmet with the goggles pushed up and the aviator sunglasses. The
   * drawings use no ids, so a chat full of them never has two elements claim the same one.
   */
  import type { Accessory } from '$domain/looks.ts';

  type Props = { kind: Accessory };
  const { kind }: Props = $props();

  const EMOJI: Partial<Record<Accessory, string>> = {
    bloom: '🌸',
    shades: '🕶️',
    tophat: '🎩',
    crown: '👑',
  };

  /** The party hat's cone, from tip (30, 6) to the brim at y = 74, and a band of it. */
  const coneAt = (y: number) => (24 * (y - 6)) / 68;
  const band = (from: number, to: number) =>
    `${30 - coneAt(from)},${from} ${30 + coneAt(from)},${from} ${30 + coneAt(to)},${to} ${30 - coneAt(to)},${to}`;
</script>

<span class="wear" data-kind={kind} aria-hidden="true">
  {#if EMOJI[kind] !== undefined}
    {EMOJI[kind]}
  {:else if kind === 'party'}
    <svg viewBox="0 0 60 80">
      <path d="M30 6 L54 74 Q30 82 6 74 Z" fill="#ff4ecd" stroke="#9c1f7a" stroke-width="2" />
      <polygon points={band(26, 34)} fill="#ffd23f" />
      <polygon points={band(46, 54)} fill="#4ef0ff" />
      <polygon points={band(64, 71)} fill="#ffd23f" />
      <circle cx="30" cy="7" r="7" fill="#fff" stroke="#d6d6d6" stroke-width="1.5" />
    </svg>
  {:else if kind === 'horns'}
    <svg viewBox="0 0 100 40">
      <path d="M20 39 C15 25 11 13 3 2 C17 7 28 19 32 36 Z" fill="#e0212e" stroke="#7a0010" />
      <path d="M80 39 C85 25 89 13 97 2 C83 7 72 19 68 36 Z" fill="#e0212e" stroke="#7a0010" />
    </svg>
  {:else if kind === 'halo'}
    <svg viewBox="0 0 100 30">
      <ellipse cx="50" cy="15" rx="41" ry="9" fill="none" stroke="#ffd84d" stroke-width="6" />
      <ellipse cx="50" cy="13" rx="35" ry="5" fill="none" stroke="#fff6c8" stroke-width="1.5" />
    </svg>
  {:else if kind === 'aviators'}
    <!-- Aviator sunglasses: teardrop lenses in a thin gold frame, the double bridge between. -->
    <svg viewBox="0 0 100 40">
      <path d="M44 7 L56 7" stroke="#d4af37" stroke-width="2.5" fill="none" />
      <path d="M46 13 Q50 10 54 13" stroke="#d4af37" stroke-width="2" fill="none" />
      <path d="M9 9 L1 6 M91 9 L99 6" stroke="#d4af37" stroke-width="2.5" />
      {#each [false, true] as right (right)}
        <g transform={right ? 'translate(100 0) scale(-1 1)' : undefined}>
          <path
            d="M8 8 C9 4 42 3 45 8 C48 16 40 32 28 33 C14 34 6 22 8 8 Z"
            fill="#25322f"
            stroke="#d4af37"
            stroke-width="2.5"
          />
          <path d="M13 10 C20 7 30 7 36 9 C31 12 21 13 15 17 Z" fill="#fff" opacity="0.28" />
          <path d="M14 24 C20 29 30 30 38 25" stroke="#8a5a2b" stroke-width="3" fill="none" opacity="0.45" />
        </g>
      {/each}
    </svg>
  {:else if kind === 'flightcap'}
    <!-- A leather flying helmet, flaps down over the ears, the goggles pushed up on the
         forehead: what flying looked like before there was a canopy. -->
    <svg viewBox="0 0 100 80">
      <path
        d="M10 52 C8 20 30 4 50 4 C70 4 92 20 90 52 L90 70 C90 75 83 77 81 72 L79 55 C68 46 32 46 21 55 L19 72 C17 77 10 75 10 70 Z"
        fill="#7b4a26"
        stroke="#3f230f"
        stroke-width="2.5"
      />
      <path d="M50 6 L50 30" stroke="#b07a4a" stroke-width="1.5" stroke-dasharray="3 2.5" />
      <path d="M22 18 C34 10 66 10 78 18" stroke="#9a6236" stroke-width="2" fill="none" />
      <path d="M11 37 L89 37" stroke="#2c2c2c" stroke-width="7" />
      {#each [35, 65] as x (x)}
        <circle cx={x} cy="35" r="12" fill="#3a3f46" />
        <circle cx={x} cy="35" r="9.5" fill="#86c8f2" stroke="#d9dee4" stroke-width="3" />
        <circle cx={x - 3} cy="32" r="3" fill="#fff" opacity="0.75" />
      {/each}
      <path d="M45 35 L55 35" stroke="#d9dee4" stroke-width="3" />
    </svg>
  {/if}
</span>

<style>
  .wear {
    position: absolute;
    z-index: 1;
    line-height: 1;
    pointer-events: none;
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  [data-kind='bloom'] {
    top: -20%;
    right: -26%;
    font-size: calc(var(--size) * 0.52);
    rotate: 18deg;
  }

  [data-kind='shades'] {
    top: 20%;
    left: 50%;
    font-size: calc(var(--size) * 0.66);
    translate: -50% 0;
  }

  [data-kind='tophat'] {
    bottom: 66%;
    left: 50%;
    font-size: calc(var(--size) * 0.7);
    translate: -50% 0;
    rotate: -10deg;
  }

  [data-kind='crown'] {
    bottom: 72%;
    left: 50%;
    font-size: calc(var(--size) * 0.62);
    translate: -50% 0;
    rotate: -6deg;
  }

  [data-kind='party'] {
    bottom: 64%;
    left: 4%;
    width: 46%;
    height: 60%;
    rotate: -20deg;
  }

  [data-kind='horns'] {
    bottom: 74%;
    left: 0;
    width: 100%;
    height: 40%;
  }

  [data-kind='halo'] {
    bottom: 92%;
    left: 50%;
    width: 82%;
    height: 24%;
    translate: -50% 0;
    filter: drop-shadow(0 0 2px #fff2a8);
  }

  [data-kind='aviators'] {
    top: 26%;
    left: 50%;
    width: 98%;
    height: 40%;
    translate: -50% 0;
  }

  [data-kind='flightcap'] {
    top: -24%;
    left: 50%;
    width: 124%;
    height: 99%;
    translate: -50% 0;
  }
</style>
